import React, { useState } from 'react';
import { Newspaper, Search, Filter, RefreshCw, Download, ExternalLink, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';

const ThreatNews = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Sample data - in production, this would come from an API
  const news = Array.from({ length: 50 }, (_, i) => ({
    id: `NEWS-${1000 + i}`,
    title: i % 4 === 0 
      ? 'Critical Zero-Day Vulnerability Discovered in Popular Software'
      : i % 4 === 1 
      ? 'New Ransomware Campaign Targeting Healthcare Sector'
      : i % 4 === 2 
      ? 'APT Group Expands Operations to New Regions'
      : 'Major Data Breach Affects Millions of Users',
    category: i % 4 === 0 ? 'vulnerability' : i % 4 === 1 ? 'malware' : i % 4 === 2 ? 'apt' : 'breach',
    source: i % 3 === 0 ? 'CyberNews' : i % 3 === 1 ? 'ThreatPost' : 'SecurityWeek',
    impact: i % 3 === 0 ? 'critical' : i % 3 === 1 ? 'high' : 'medium',
    image: i % 4 === 0 
      ? 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=400&h=300&fit=crop'
      : i % 4 === 1 
      ? 'https://images.unsplash.com/photo-1576444356170-66073046b1bc?w=400&h=300&fit=crop'
      : i % 4 === 2 
      ? 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=300&fit=crop'
      : 'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=400&h=300&fit=crop',
    publishedAt: new Date(Date.now() - Math.random() * 10000000000).toISOString(),
    relatedAssets: Math.floor(Math.random() * 10)
  }));

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedNews = news.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Newspaper className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Threat Intelligence News</h1>
            <p className="text-sm text-gray-400">Stay updated with the latest cybersecurity threats and incidents</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
          <input
            type="text"
            placeholder="Search news..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#1A1F2E] border border-gray-800 rounded-lg text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          />
        </div>
        <Button variant="outline">
          <Filter className="h-4 w-4 mr-2" />
          Filter
        </Button>
      </div>

      {/* News Grid */}
      <div className="grid grid-cols-1 gap-6">
        {displayedNews.map((item) => (
          <div key={item.id} className="bg-[#0F1629] rounded-xl border border-gray-800/50 overflow-hidden hover:border-gray-700/50 transition-colors">
            <div className="flex p-6">
              <div className="flex-shrink-0">
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="w-48 h-32 rounded-lg object-cover"
                  loading="lazy"
                />
              </div>
              <div className="flex-1 ml-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-white">{item.title}</h3>
                    <div className="flex items-center space-x-3 mt-2">
                      <Badge variant={item.impact as any}>{item.impact}</Badge>
                      <Badge variant="outline">{item.category}</Badge>
                      <span className="text-sm text-gray-400">{item.source}</span>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Read More
                  </Button>
                </div>
                <div className="mt-4 flex items-center justify-between text-sm text-gray-400">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      {new Date(item.publishedAt).toLocaleString()}
                    </div>
                  </div>
                  {item.relatedAssets > 0 && (
                    <div>
                      {item.relatedAssets} related assets
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between bg-[#0F1629] rounded-xl border border-gray-800/50 px-6 py-4">
        <div className="text-sm text-gray-400">
          Showing {startIndex + 1} to {Math.min(endIndex, news.length)} of {news.length} news items
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span className="px-3 py-1 rounded-lg bg-blue-500/10 text-blue-500">
            {currentPage}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => p + 1)}
            disabled={endIndex >= news.length}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ThreatNews;
